import { Button } from '@heroui/react';
import { Upload } from 'lucide-react';
import React, { FC } from 'react';

import { useHeroToast } from '../../../../../hooks';
import { AdAsset, useDeleteAdAssets, useGetAdAssets } from '../../../../../queries';
import { useAssetUpload } from '../../../hooks';
import { createImageElement } from '../../../model/factories';
import { useAdsGeneratorStore } from '../../../model/store';
import { PanelSection } from '../../common';
import { AssetActions, AssetGrid, AssetThumb, AssetTile, EmptyPanelHint } from '../Editor.styled';
import { LibraryPanel } from './LibraryPanel';

/** Zdjęcie bez zapisanych wymiarów wstawiamy w rozsądnym kwadracie. */
const FALLBACK_SIZE = 800;

export const ImagesPanel: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const addElement = useAdsGeneratorStore((state) => state.addElement);
  const setBackground = useAdsGeneratorStore((state) => state.setBackground);
  const { inputRef, accept, isUploading, openPicker, handleFiles } = useAssetUpload();
  const { data: assets = [], isLoading } = useGetAdAssets();
  const { mutate: removeAssets } = useDeleteAdAssets();
  const { showError } = useHeroToast();

  if (!scene) return null;

  const insert = (asset: AdAsset): void => {
    addElement(createImageElement(
      asset.url,
      asset.width || FALLBACK_SIZE,
      asset.height || FALLBACK_SIZE,
      scene.width,
      scene.height,
    ));
  };

  return (
    <>
      <PanelSection title="Moje obrazy">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={async (event) => {
            const uploaded = await handleFiles(event.target.files);
            if (uploaded.length === 1) insert(uploaded[0]);
          }}
        />
        <Button
          size="sm"
          radius="sm"
          variant="flat"
          color="primary"
          startContent={<Upload size={16} />}
          isLoading={isUploading}
          onPress={openPicker}
        >
          Wgraj plik
        </Button>

        {assets.length === 0 ? (
          <EmptyPanelHint>
            {isLoading
              ? 'Wczytuję bibliotekę zdjęć...'
              : 'Obsługujemy PNG, JPG, WEBP i SVG do 8 MB. Pliki trafiają na stałe do katalogu hotelspot-admin-ads i zostają w bibliotece do kolejnych kreacji.'}
          </EmptyPanelHint>
        ) : (
          <AssetGrid>
            {assets.map((asset) => (
              <AssetTile key={asset.id}>
                <AssetThumb type="button" title={asset.name || 'Wstaw na planszę'} onClick={() => insert(asset)}>
                  <img src={asset.url} alt="" crossOrigin="anonymous" />
                </AssetThumb>
                <AssetActions>
                  <button
                    type="button"
                    title="Ustaw jako tło planszy"
                    onClick={() => setBackground({ kind: 'image', imageSrc: asset.url })}
                  >
                    tło
                  </button>
                  <button
                    type="button"
                    title="Usuń z biblioteki (plik w Bytescale zostaje)"
                    onClick={() => removeAssets(
                      { ids: [asset.id] },
                      { onError: (error) => showError(error.message || 'Nie udało się usunąć zdjęcia') },
                    )}
                  >
                    usuń
                  </button>
                </AssetActions>
              </AssetTile>
            ))}
          </AssetGrid>
        )}
      </PanelSection>

      <LibraryPanel />
    </>
  );
};
